import { h } from '../utils/dom.ts';
import { getLocale, t } from '../i18n/index.ts';
import { PLATFORM } from '../config/platform.ts';
import type { LegalPage } from '../router.ts';
import type { Business } from '../utils/validation.ts';

/**
 * Impressum (§ 5 DDG) and privacy notice (Art. 13 DSGVO), generated from the
 * shop's business.json. German only — that is the legally relevant version.
 *
 * TEMPLATE TEXT: have it reviewed by a lawyer before going live.
 */

const RETENTION_MONTHS = 12;

export function legalPage(business: Business | null, page: LegalPage): HTMLElement {
  return h('div', { class: 'page legal', lang: 'de' },
    getLocale() !== 'de' && h('p', { class: 'notice notice-warn' }, 'This page is only available in German.'),
    page === 'imprint' ? imprint(business) : privacy(business),
  );
}

const p = (...lines: (string | false | undefined)[]) =>
  h('p', null, lines.filter((l): l is string => Boolean(l)).flatMap((l, i) => (i ? [h('br'), l] : [l])));

function imprint(b: Business | null): HTMLElement {
  if (!b) {
    return h('div', null,
      h('h1', null, t('footer.imprint')),
      h('h2', null, 'Angaben gemäß § 5 DDG'),
      p(PLATFORM.operator, PLATFORM.address),
      h('h2', null, 'Kontakt'),
      p(PLATFORM.phone && `Telefon: ${PLATFORM.phone}`, `E-Mail: ${PLATFORM.email}`),
      PLATFORM.vatId ? h('div', null, h('h2', null, 'Umsatzsteuer-ID'), p(`Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: ${PLATFORM.vatId}`)) : null,
      disputeResolution(),
    );
  }
  const { legal, contact, location } = b;
  return h('div', null,
    h('h1', null, t('footer.imprint')),
    h('h2', null, 'Angaben gemäß § 5 DDG'),
    p(legal.company ?? b.name, location.address),
    p(legal.company ? `Vertreten durch: ${legal.owner}` : `Inhaber:in: ${legal.owner}`),
    h('h2', null, 'Kontakt'),
    p(`Telefon: ${contact.phone}`, `E-Mail: ${contact.email}`, contact.website && `Website: ${contact.website}`),
    legal.register ? h('div', null, h('h2', null, 'Registereintrag'), p(legal.register)) : null,
    legal.vat_id ? h('div', null, h('h2', null, 'Umsatzsteuer-ID'), p(`Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: ${legal.vat_id}`)) : null,
    disputeResolution(),
    h('h2', null, 'Technische Umsetzung'),
    p(`Das Online-Buchungssystem wird bereitgestellt von ${PLATFORM.operator}, ${PLATFORM.address}.`),
  );
}

function disputeResolution(): HTMLElement {
  return h('div', null,
    h('h2', null, 'Verbraucherstreitbeilegung'),
    p('Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'),
  );
}

function privacy(b: Business | null): HTMLElement {
  const controller = b
    ? [b.legal.company ?? b.name, b.legal.company ? `vertreten durch ${b.legal.owner}` : `Inhaber:in: ${b.legal.owner}`, b.location.address, `Telefon: ${b.contact.phone}`, `E-Mail: ${b.contact.email}`]
    : [PLATFORM.operator, PLATFORM.address, `E-Mail: ${PLATFORM.email}`];
  const section = (title: string, ...body: (Node | string | null)[]) => h('section', null, h('h2', null, title), ...body);

  return h('div', null,
    h('h1', null, 'Datenschutzerklärung'),
    h('p', { class: 'muted' }, 'Stand: Oktober 2026'),
    section('1. Verantwortlicher', p(...controller)),
    b ? section('2. Technischer Dienstleister',
      p(`Das Buchungssystem wird im Auftrag des oben genannten Salons von ${PLATFORM.operator}, ${PLATFORM.address}, als Auftragsverarbeiter (Art. 28 DSGVO) betrieben.`),
    ) : null,
    section(b ? '3. Terminbuchung' : '2. Terminbuchung über diese Plattform',
      p('Wenn du einen Termin buchst, verarbeiten wir: Name, Telefonnummer, optional E-Mail-Adresse, die gewählte Leistung, Barber, Datum und Uhrzeit sowie den Status des Termins.'),
      p('Zweck: Durchführung und Verwaltung des Termins, Bestätigungs- und Erinnerungs-E-Mails (falls E-Mail angegeben), Rückfragen bei Änderungen. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertrag bzw. vorvertragliche Maßnahmen).'),
      p('Zum Schutz vor Missbrauch begrenzen wir die Anzahl der Buchungen pro Telefonnummer (Art. 6 Abs. 1 lit. f DSGVO, berechtigtes Interesse am störungsfreien Betrieb).'),
      p(`Personenbezogene Buchungsdaten werden spätestens ${RETENTION_MONTHS} Monate nach dem Termin anonymisiert, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen. Anonymisierte Daten (Leistung, Zeitpunkt, Preis) nutzen wir für Statistiken.`),
    ),
    section('Speicherung im Browser',
      p('Wir verwenden keine Cookies und kein Tracking. Im lokalen Speicher deines Browsers (localStorage) legen wir ab: die gewählte Sprache und Referenzen auf deine Buchungen, damit du sie unter „Meine Termine“ sehen und stornieren kannst (§ 25 Abs. 2 Nr. 2 TDDDG). Deine Kontaktdaten speichern wir dort nur, wenn du „Angaben auf diesem Gerät merken“ wählst (§ 25 Abs. 1 TDDDG, Einwilligung). Du kannst diese Daten jederzeit über die Browsereinstellungen löschen.'),
    ),
    section('Hosting und Dienstleister',
      p('Beim Aufruf der Website verarbeitet unser Hosting-Anbieter technisch notwendige Daten (z. B. IP-Adresse, Zeitpunkt, aufgerufene Seite), um die Seite auszuliefern und vor Angriffen zu schützen (Art. 6 Abs. 1 lit. f DSGVO).'),
      h('ul', null,
        h('li', null, PLATFORM.processors.hosting),
        h('li', null, PLATFORM.processors.database),
        h('li', null, PLATFORM.processors.email),
      ),
      p('Mit allen Dienstleistern bestehen Verträge zur Auftragsverarbeitung. Soweit Daten in die USA übermittelt werden können, erfolgt dies auf Grundlage des EU-US Data Privacy Framework bzw. von EU-Standardvertragsklauseln.'),
    ),
    section('Deine Rechte',
      p('Du hast das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch (Art. 21). Wende dich dazu an den oben genannten Verantwortlichen.'),
      p('Du hast außerdem das Recht, dich bei einer Datenschutz-Aufsichtsbehörde zu beschweren (Art. 77 DSGVO).'),
      p('Eine automatisierte Entscheidungsfindung einschließlich Profiling findet nicht statt.'),
    ),
  );
}
